import React from 'react';
import { Field } from 'react-final-form';
import unionBy from 'lodash/unionBy';
import isEqual from 'lodash/isEqual';

import { showErrorOnBlurOrDirty, AutocompleteOption } from '@occc-app/design-system';
import { useCanEditFormFieldsContext } from '@occc-app/App/CanEditFormFieldsContext';

import {
  ILazyAutocompleteFieldProps,
  ILazyAutocompleteProps,
  AutocompleteOptionType,
} from './lazy-autocomplete.types';
import {
  mapAutocompleteOptionToRawResponseItem,
  useLazyAutocompleteLogic,
} from './lazy-autocomplete.utils';

import { Autocomplete } from '../autocomplete/autocomplete.component';

export function LazyAutocomplete<T = any, R = any>({
  value,
  errorText,
  mapRawResponseItemToAutocompleteOption,
  mapQueryResponseToRawArray,
  makeQueryFilter,
  useLazyQuery,
  idExcludes = [],
  excludeSelectedItems = false,
  ...props
}: ILazyAutocompleteProps<T, R>) {
  const {
    loading,
    renderInput,
    inputValue,
    onInputChange,
    responseOptionsArray,
    hasFilterOptions,
  } = useLazyAutocompleteLogic({
    ...props,
    errorText,
    useLazyQuery,
    makeQueryFilter,
    mapQueryResponseToRawArray,
    mapRawResponseItemToAutocompleteOption,
  });

  const { isEditable } = useCanEditFormFieldsContext();

  const autocompleteValue = React.useMemo(
    () => (value || []).map(mapRawResponseItemToAutocompleteOption),
    [value, mapRawResponseItemToAutocompleteOption],
  );

  const options: AutocompleteOptionType[] = React.useMemo(() => {
    const uniqueOptions = unionBy(responseOptionsArray, autocompleteValue, 'value');

    let filteredOptions = uniqueOptions;

    if (idExcludes.length) {
      filteredOptions = filteredOptions.filter(({ value }) => !idExcludes.includes(value));
    }

    if (excludeSelectedItems) {
      const selectedIds = new Set(autocompleteValue.map((option) => option.value));
      filteredOptions = filteredOptions.filter(({ value }) => !selectedIds.has(value));
    }

    return filteredOptions;
  }, [responseOptionsArray, autocompleteValue, idExcludes, excludeSelectedItems]);

  const { onChange } = props;
  const onChangeMemo = React.useCallback(
    (_event: any, newValue: AutocompleteOption[]) => {
      onChange(newValue.map(mapAutocompleteOptionToRawResponseItem));
    },
    [onChange],
  );

  const getOptionSelected = React.useCallback(
    (option: AutocompleteOption, currentValue: AutocompleteOption) =>
      option.value === currentValue.value,
    [],
  );

  const disabled = props.loading || props.disabled || !isEditable;

  return (
    <Autocomplete
      {...props}
      blurOnSelect={true}
      multiple={true}
      inputValue={inputValue}
      onInputChange={onInputChange}
      value={autocompleteValue}
      onChange={onChangeMemo}
      options={options}
      isOptionEqualToValue={getOptionSelected}
      filterOptions={hasFilterOptions ? props.filterOptions : undefined}
      renderInput={renderInput}
      disabled={disabled}
      loading={props.loading || loading}
    />
  );
}

export function LazyAutocompleteField<T = any, R = any>(props: ILazyAutocompleteFieldProps<T, R>) {
  const renderInput = ({ input, meta }: any) => {
    const isError = showErrorOnBlurOrDirty({ meta });

    return (
      <LazyAutocomplete<T, R>
        {...props}
        errorText={isError ? meta.error : ''}
        value={input.value || []}
        onChange={input.onChange}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
      />
    );
  };
  return <Field name={props.name} isEqual={isEqual} render={renderInput} />;
}
